import { useState } from "react";
import {
  AccessPanel,
  AerosolSeparator,
  AirHandlingUnit,
  AluminiumMeshGreaseFilter,
  Anemostat,
  BuildUp,
  Component,
  Construction,
  DummyPlate,
  ExhaustDuctAngular,
  ExhaustDuctRound,
  ExhaustHood,
  ExhaustHoodSystem,
  ExtraHours,
  FreeText,
  Kind,
  Lighting,
  PassiveArea,
  SupplyDuctAngular,
  SupplyDuctRound,
  VentilatedCeiling,
  VentilatedCeilingSystem,
} from "@/services/backend/vbs/treatments/types/component";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  UpdateComponentRequest,
  useVbsTreatmentUpdateActuallyTreatedMutation,
  useVbsTreatmentUpdateComponentMutation,
} from "@/services/backend/vbs/treatments/api/treatment-api";
import {
  FloatNumberInput,
  IntNumberInput,
} from "@/routes/processes/_components/ui/number-input";
import t from "@/lang/lang";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

export function ComponentCell({
  treatmentId,
  positionId,
  component: propComponent,
  actuallyTreated = false,
  disabled = false,
}: {
  treatmentId: string;
  positionId: string;
  component: Component;
  actuallyTreated?: boolean;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<UpdateComponentRequest>({
    id: treatmentId,
    positionId,
    component: propComponent,
  });
  const componentMutation = useVbsTreatmentUpdateComponentMutation();
  const actuallyTreatedMutation =
    useVbsTreatmentUpdateActuallyTreatedMutation();
  useDebouncedMutation(
    request,
    setRequest,
    actuallyTreated ? actuallyTreatedMutation : componentMutation,
    {
      id: treatmentId,
      positionId,
      component: propComponent,
    },
  );

  switch (propComponent.kind) {
    case Kind.AccessPanel:
      return (
        <ComponentAccessPanel
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.AerosolSeparator:
      return (
        <ComponentAerosolSeparator
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.AirHandlingUnit:
      return (
        <ComponentAirHandlingUnit
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.AluminiumMeshGreaseFilter:
      return (
        <ComponentAluminiumMeshGreaseFilter
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.Anemostat:
      return (
        <ComponentAnemostat
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.DummyPlate:
      return (
        <ComponentDummyPlate
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.ExhaustDuctAngular:
      return (
        <ComponentExhaustDuctAngular
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.ExhaustDuctRound:
      return (
        <ComponentExhaustDuctRound
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.ExhaustHoodSystem:
      return (
        <ComponentExhaustHoodSystem
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.ExhaustHood:
      return (
        <ComponentExhaustHood
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.ExtraHours:
      return (
        <ComponentExtraHours
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.FreeText:
      return (
        <ComponentFreeText
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.Lighting:
      return (
        <ComponentLighting
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.PassiveArea:
      return (
        <ComponentPassiveArea
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.SupplyDuctAngular:
      return (
        <ComponentSupplyDuctAngular
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.SupplyDuctRound:
      return (
        <ComponentSupplyDuctRound
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.VentilatedCeilingSystem:
      return (
        <ComponentVentilatedCeilingSystem
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    case Kind.VentilatedCeiling:
      return (
        <ComponentVentilatedCeiling
          component={request.component}
          onChange={(component) => setRequest({ ...request, component })}
          disabled={disabled}
        />
      );
    default:
      return null;
  }
}

function ComponentAccessPanel(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as AccessPanel;
  return (
    <div className="flex w-32 justify-between">
      <span className="flex h-6 items-center justify-end whitespace-nowrap">
        {t("Anzahl:")}
      </span>
      <IntNumberInput
        value={data.count}
        disabled={disabled}
        onChange={(count) =>
          onChange({
            kind: component.kind,
            data: { count },
          })
        }
      />
    </div>
  );
}

function ComponentAerosolSeparator(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as AerosolSeparator;
  return (
    <div className="flex w-32 justify-between">
      <span className="flex h-6 items-center justify-end whitespace-nowrap">
        {t("Anzahl:")}
      </span>
      <IntNumberInput
        value={data.count}
        disabled={disabled}
        onChange={(count) =>
          onChange({
            kind: component.kind,
            data: { count },
          })
        }
      />
    </div>
  );
}

function ComponentAirHandlingUnit(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as AirHandlingUnit;
  return (
    <div>
      <div className="flex w-32 justify-between">
        <span className="flex h-6 items-center justify-end whitespace-nowrap">
          {t("Zeit (h):")}
        </span>
        <FloatNumberInput
          value={data.cleanTime}
          disabled={disabled}
          onChange={(cleanTime) =>
            onChange({
              ...component,
              data: { ...data, cleanTime },
            })
          }
        />
      </div>
      <div className="my-1">
        <hr />
      </div>
      <div className="flex justify-between gap-1">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("LxBxH (mm):")}
        </span>
        <IntNumberInput
          value={data.length}
          disabled={disabled}
          onChange={(length) =>
            onChange({
              kind: component.kind,
              data: { ...data, length },
            })
          }
        />
        <IntNumberInput
          value={data.width}
          disabled={disabled}
          onChange={(width) =>
            onChange({
              kind: component.kind,
              data: { ...data, width },
            })
          }
        />
        <IntNumberInput
          value={data.height}
          disabled={disabled}
          onChange={(height) =>
            onChange({
              kind: component.kind,
              data: { ...data, height },
            })
          }
        />
      </div>
      <div>
        <span className="text-sx text-gray-500">
          {t("Hat keinen Einfluss auf die Zeit.")}
        </span>
      </div>
    </div>
  );
}

function ComponentAluminiumMeshGreaseFilter(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as AluminiumMeshGreaseFilter;
  return (
    <div className="flex w-32 justify-between">
      <span className="flex h-6 items-center justify-end whitespace-nowrap">
        {t("Anzahl:")}
      </span>
      <IntNumberInput
        value={data.count}
        disabled={disabled}
        onChange={(count) =>
          onChange({
            kind: component.kind,
            data: { count },
          })
        }
      />
    </div>
  );
}

function ComponentAnemostat(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as Anemostat;
  return (
    <div className="flex w-32 justify-between">
      <span className="flex h-6 items-center justify-end whitespace-nowrap">
        {t("Anzahl:")}
      </span>
      <IntNumberInput
        value={data.count}
        disabled={disabled}
        onChange={(count) =>
          onChange({
            kind: component.kind,
            data: { count },
          })
        }
      />
    </div>
  );
}

function ComponentDummyPlate(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as DummyPlate;
  return (
    <div className="flex w-32 justify-between">
      <span className="flex h-6 items-center justify-end whitespace-nowrap">
        {t("Anzahl:")}
      </span>
      <IntNumberInput
        value={data.count}
        disabled={disabled}
        onChange={(count) =>
          onChange({
            kind: component.kind,
            data: { count },
          })
        }
      />
    </div>
  );
}

function ComponentExhaustDuctAngular(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as ExhaustDuctAngular;
  return (
    <div>
      <div className="flex items-center space-x-4">
        <div className="flex w-32 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Länge (m):")}
          </span>
          <FloatNumberInput
            value={data.length}
            disabled={disabled}
            onChange={(length) =>
              onChange({
                kind: component.kind,
                data: { ...data, length },
              })
            }
          />
        </div>
        <div className="flex grow justify-end space-x-4">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("BuildUp")}:
          </span>
          <BuildUpSelect
            value={data.buildUp}
            disabled={disabled}
            onChange={(buildUp) =>
              onChange({
                kind: component.kind,
                data: { ...data, buildUp },
              })
            }
          />
        </div>
      </div>
      <div className="my-1">
        <hr />
      </div>
      <div className="flex space-x-4">
        <div className="flex w-32 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Höhe (mm):")}
          </span>
          <IntNumberInput
            value={data.height}
            disabled={disabled}
            onChange={(height) =>
              onChange({
                kind: component.kind,
                data: { ...data, height },
              })
            }
          />
        </div>
        <div className="flex w-24 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Breite (mm):")}
          </span>
          <IntNumberInput
            value={data.width}
            disabled={disabled}
            onChange={(width) =>
              onChange({
                kind: component.kind,
                data: { ...data, width },
              })
            }
          />
        </div>
      </div>
      <div className="">
        <span className="text-sx text-gray-500">
          {t("Höhe und Breite haben keinen Einfluss auf die Zeit.")}
        </span>
      </div>
    </div>
  );
}

function BuildUpSelect({
  value,
  onChange,
  disabled = false,
}: {
  value: BuildUp;
  onChange: (e: BuildUp) => void;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-6 w-28">
        <SelectValue>{t(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>{t("Verschmutzung")}</SelectLabel>
          {Object.values(BuildUp).map((buildUp) => (
            <SelectItem value={buildUp} key={buildUp}>
              {t(buildUp)}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function ComponentExhaustDuctRound(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as ExhaustDuctRound;
  return (
    <div>
      <div className="flex space-x-4">
        <div className="flex w-32 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Länge (m):")}
          </span>
          <FloatNumberInput
            value={data.length}
            disabled={disabled}
            onChange={(length) =>
              onChange({
                kind: component.kind,
                data: { ...data, length },
              })
            }
          />
        </div>
        <div className="flex grow justify-end space-x-4">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("BuildUp")}:
          </span>
          <BuildUpSelect
            value={data.buildUp}
            disabled={disabled}
            onChange={(buildUp) =>
              onChange({
                kind: component.kind,
                data: { ...data, buildUp },
              })
            }
          />
        </div>
      </div>
      <div className="my-1">
        <hr />
      </div>
      <div className="flex space-x-4">
        <div className="flex w-48 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Durchmesser (mm):")}
          </span>
          <IntNumberInput
            value={data.diameter}
            disabled={disabled}
            onChange={(diameter) =>
              onChange({
                kind: component.kind,
                data: { ...data, diameter },
              })
            }
          />
        </div>
      </div>
      <div className="">
        <span className="text-sx text-gray-500">
          {t("Durchmesser hat keinen Einfluss auf die Zeit.")}
        </span>
      </div>
    </div>
  );
}

function ComponentExhaustHoodSystem(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as ExhaustHoodSystem;
  return (
    <div>
      <ComponentExhaustHood
        component={data.exhaustHood}
        disabled={disabled}
        onChange={(exhaustHood) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              exhaustHood,
            },
          })
        }
      />
      <ComponentAerosolSeparator
        component={data.aerosolSeparator}
        disabled={disabled}
        onChange={(aerosolSeparator) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              aerosolSeparator,
            },
          })
        }
      />
      <ComponentAluminiumMeshGreaseFilter
        component={data.aluminiumMeshGreaseFilter}
        disabled={disabled}
        onChange={(aluminiumMeshGreaseFilter) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              aluminiumMeshGreaseFilter,
            },
          })
        }
      />
      <ComponentLighting
        component={data.lighting}
        disabled={disabled}
        onChange={(lighting) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              lighting,
            },
          })
        }
      />
      <ComponentDummyPlate
        component={data.dummyPlate}
        disabled={disabled}
        onChange={(dummyPlate) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              dummyPlate,
            },
          })
        }
      />
    </div>
  );
}

function ComponentExhaustHood(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as ExhaustHood;
  return (
    <div className="flex space-x-4">
      <div className="flex w-32 justify-between">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Länge (m):")}
        </span>
        <FloatNumberInput
          value={data.length}
          disabled={disabled}
          onChange={(length) =>
            onChange({
              kind: component.kind,
              data: { ...data, length },
            })
          }
        />
      </div>
      <div className="flex w-32 justify-between">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Breite (m):")}
        </span>
        <FloatNumberInput
          value={data.width}
          disabled={disabled}
          onChange={(width) =>
            onChange({
              kind: component.kind,
              data: { ...data, width },
            })
          }
        />
      </div>
    </div>
  );
}

function ComponentExtraHours(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as ExtraHours;
  return (
    <div className="flex w-32 justify-between">
      <span className="flex h-6 items-center whitespace-nowrap">
        {t("Stunden:")}
      </span>
      <IntNumberInput
        value={data.hours}
        disabled={disabled}
        onChange={(hours) =>
          onChange({
            kind: component.kind,
            data: { hours },
          })
        }
      />
    </div>
  );
}

function ComponentFreeText(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as FreeText;
  return (
    <div>
      <div className="mb-2">
        <input
          className="min-w-[200px] rounded border border-gray-300 px-1 py-0.5"
          value={data.text}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              kind: component.kind,
              data: { ...data, text: e.target.value },
            })
          }
        />
      </div>
      <div className="flex w-full justify-between">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Euro:")}
        </span>
        <FloatNumberInput
          value={data.price}
          disabled={disabled}
          onChange={(price) =>
            onChange({
              kind: component.kind,
              data: { ...data, price },
            })
          }
          width={32}
          fractions={2}
        />
      </div>
    </div>
  );
}

function ComponentLighting(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as Lighting;
  return (
    <div className="flex w-32 justify-between">
      <span className="flex h-6 items-center whitespace-nowrap">
        {t("Anzahl:")}
      </span>
      <IntNumberInput
        value={data.count}
        disabled={disabled}
        onChange={(count) =>
          onChange({
            kind: component.kind,
            data: { count },
          })
        }
      />
    </div>
  );
}

function ComponentPassiveArea(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as PassiveArea;
  return (
    <div className="flex space-x-4">
      <div className="flex w-32 justify-between">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Länge (m):")}
        </span>
        <FloatNumberInput
          value={data.length}
          disabled={disabled}
          onChange={(length) =>
            onChange({
              kind: component.kind,
              data: { ...data, length },
            })
          }
        />
      </div>
      <div className="flex w-32 justify-between">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Breite (m):")}
        </span>
        <FloatNumberInput
          value={data.width}
          disabled={disabled}
          onChange={(width) =>
            onChange({
              kind: component.kind,
              data: { ...data, width },
            })
          }
        />
      </div>
    </div>
  );
}

function ComponentSupplyDuctAngular(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as SupplyDuctAngular;
  return (
    <div>
      <div className="flex space-x-4">
        <div className="flex w-32 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Länge (m):")}
          </span>
          <FloatNumberInput
            value={data.length}
            disabled={disabled}
            onChange={(length) =>
              onChange({
                kind: component.kind,
                data: { ...data, length },
              })
            }
          />
        </div>
      </div>
      <div className="my-1">
        <hr />
      </div>
      <div className="flex space-x-4">
        <div className="flex w-32 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Höhe (mm):")}
          </span>
          <IntNumberInput
            value={data.height}
            disabled={disabled}
            onChange={(height) =>
              onChange({
                kind: component.kind,
                data: { ...data, height },
              })
            }
          />
        </div>
        <div className="flex w-24 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Breite (mm):")}
          </span>
          <IntNumberInput
            value={data.width}
            disabled={disabled}
            onChange={(width) =>
              onChange({
                kind: component.kind,
                data: { ...data, width },
              })
            }
          />
        </div>
      </div>
      <div className="">
        <span className="text-sx text-gray-500">
          {t("Höhe und Breite haben keinen Einfluss auf die Zeit.")}
        </span>
      </div>
    </div>
  );
}

function ComponentSupplyDuctRound(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as SupplyDuctRound;
  return (
    <div>
      <div className="flex space-x-4">
        <div className="flex w-32 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Länge (m):")}
          </span>
          <FloatNumberInput
            value={data.length}
            disabled={disabled}
            onChange={(length) =>
              onChange({
                kind: component.kind,
                data: { ...data, length },
              })
            }
          />
        </div>
      </div>
      <div className="my-1">
        <hr />
      </div>
      <div className="flex space-x-4">
        <div className="flex w-48 justify-between">
          <span className="flex h-6 items-center whitespace-nowrap">
            {t("Durchmesser (mm):")}
          </span>
          <IntNumberInput
            value={data.diameter}
            disabled={disabled}
            onChange={(diameter) =>
              onChange({
                kind: component.kind,
                data: { ...data, diameter },
              })
            }
          />
        </div>
      </div>
      <div className="">
        <span className="text-sx text-gray-500">
          {t("Durchmesser hat keinen Einfluss auf die Zeit.")}
        </span>
      </div>
    </div>
  );
}

function ComponentVentilatedCeilingSystem(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as VentilatedCeilingSystem;
  return (
    <div>
      <ComponentVentilatedCeiling
        component={data.ventilatedCeiling}
        disabled={disabled}
        onChange={(ventilatedCeiling) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              ventilatedCeiling,
            },
          })
        }
      />
      <ComponentAerosolSeparator
        component={data.aerosolSeparator}
        disabled={disabled}
        onChange={(aerosolSeparator) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              aerosolSeparator,
            },
          })
        }
      />
      <ComponentLighting
        component={data.lighting}
        disabled={disabled}
        onChange={(lighting) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              lighting,
            },
          })
        }
      />
      <ComponentDummyPlate
        component={data.dummyPlate}
        disabled={disabled}
        onChange={(dummyPlate) =>
          onChange({
            kind: component.kind,
            data: {
              ...data,
              dummyPlate,
            },
          })
        }
      />
    </div>
  );
}

function ComponentVentilatedCeiling(props: {
  component: Component;
  onChange: (component: Component) => void;
  disabled?: boolean;
}) {
  const { component, onChange, disabled = false } = props;
  const data = component.data as VentilatedCeiling;
  return (
    <div className="flex space-x-4">
      <div className="flex w-32 justify-between">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Länge (m):")}
        </span>
        <FloatNumberInput
          value={data.length}
          disabled={disabled}
          onChange={(length) =>
            onChange({
              kind: component.kind,
              data: { ...data, length },
            })
          }
        />
      </div>
      <div className="flex w-32 justify-between">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Breite (m):")}
        </span>
        <FloatNumberInput
          value={data.width}
          disabled={disabled}
          onChange={(width) =>
            onChange({
              kind: component.kind,
              data: { ...data, width },
            })
          }
        />
      </div>
      <div className="flex grow justify-end space-x-4">
        <span className="flex h-6 items-center whitespace-nowrap">
          {t("Construction")}
        </span>
        <ConstructionSelect
          value={data.construction}
          disabled={disabled}
          onChange={(construction) => {
            onChange({
              kind: component.kind,
              data: { ...data, construction },
            });
          }}
        />
      </div>
    </div>
  );
}

function ConstructionSelect({
  value,
  onChange,
  disabled = false,
}: {
  value: Construction;
  onChange: (e: Construction) => void;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-6 min-w-16">
        <SelectValue placeholder={t("Auswählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>{t("Bauart")}</SelectLabel>
          {Object.values(Construction).map((construction) => (
            <SelectItem value={construction} key={construction}>
              {t(construction)}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
